'use client';

import type { FeaturedService } from '@/types/content';
import { PathEnum } from '@/types/enums';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import Button from '../ui/Button';
import H3 from '../ui/H3';
import Link from '../ui/Link';
import TextSmall from '../ui/TextSmall';

export default function FeaturedServicesSectionView({
    featuredServices,
}: {
    featuredServices: FeaturedService[];
}) {
    const t = useTranslations();

    return (
        <section className="w-full bg-blue-grey flex flex-col py-[120px] max-lg:py-20">
            <div className="w-full container mx-auto flex justify-between max-lg:flex-col gap-4 max-lg:px-4 lg:px-0">
                <h3 className="text-white max-lg:text-h3 text-h1">
                    {t('Metadata.Homepage.headings.featuredServices')}
                </h3>
                <Link
                    className="more-services-trigger"
                    href={PathEnum.Services}
                    ariaLabel={t('AllServices.moreServices')}
                >
                    <>
                        <span className="sr-only">{t('AllServices.moreServices')}</span>
                        <Button title={t('AllServices.moreServices')} withArrow />
                    </>
                </Link>
            </div>
            <div className="container mx-auto flex justify-center lg:justify-between w-full max-lg:flex-col mt-[60px] max-lg:mt-7 max-lg:gap-[60px] lg:gap-10 2xl:gap-[90px] max-lg:px-4 lg:px-0">
                {featuredServices.map(({ imageSrc, title, link }, index) => (
                    <Service imageSrc={imageSrc} title={title} link={link} key={index} />
                ))}
            </div>
        </section>
    );
}

const Service = ({ title, imageSrc, link }: { title: string; imageSrc: string; link: string }) => {
    const t = useTranslations();

    return (
        <div className="relative w-1/3 max-lg:w-full flex flex-col group gap-4">
            <div className="relative h-[294px] sm:h-[375px] w-full overflow-hidden">
                <Image
                    className="object-cover group-hover:scale-125 transition-all duration-1000 ease-in-out"
                    fill
                    src={imageSrc}
                    alt="background"
                />
            </div>
            <TextSmall className="text-grey">{t('HomePage.precisionPart')}</TextSmall>
            <div className="flex justify-between items-center flex-wrap gap-2">
                <H3 className="text-white max-lg:text-h4 max-xs:text-h5">
                    {t(`Services.${title}`)}
                </H3>
                <Link className="w-fit" href={link} ariaLabel={t('AllServices.readMore')}>
                    <>
                        <span className="sr-only">{t('AllServices.readMore')}</span>
                        <Button title={t('AllServices.readMore')} withArrow />
                    </>
                </Link>
            </div>
        </div>
    );
};
